import { useEffect } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { subscriptionService, OrderItemsParams } from '@/services/subscriptionService'
import { queryKeys } from '@/services/queryClient'
import { BillingCycleType, ItemStatus } from '@/constants'
import type { Subscription } from '@/types'
import { isRecurring, sortItems } from '@utils'
import { preloadImages } from '@/utils/imageCache'
import { toCrossSellProducts } from '../utils/crossSell'
import { hasFailedRebill, isLatestCycle, isSupersededCycle } from '../utils/portalActions'
import { resolveProductImage } from '../utils/omp'
import {
  crossSellQuery,
  LATEST_CYCLE_PARAMS,
  orderGroupParams,
  orderItemsQuery,
  PAYMENT_ISSUE_PARAMS,
  PORTAL_CACHE,
  portalItemQuery,
} from '../utils/portalQueries'

// Only these four end a subscription — recycle/decline/paused states stay active.
const INACTIVE_SUB_STATUSES: number[] = [
  ItemStatus.Cancelled,
  ItemStatus.Complete,
  ItemStatus.Replaced,
  ItemStatus.ThirdPartyCancel,
]

export function isActiveSubscription(item: Subscription): boolean {
  return !INACTIVE_SUB_STATUSES.includes(Number(item.status))
}

// Fetch + decode the thumbnails the cards are about to render, so the <img> paints with
// the frame that lays it out rather than whenever the browser gets round to decoding.
function usePreloadedThumbnails(items: Subscription[] | undefined) {
  const urls = (items ?? []).map(item => resolveProductImage(item)).join('\n')
  useEffect(() => {
    if (urls) {
      preloadImages(urls.split('\n'))
    }
  }, [urls])
}

export function useOrderItems(params: OrderItemsParams, enabled = true) {
  const query = useQuery({ ...orderItemsQuery(params), enabled })
  usePreloadedThumbnails(query.data)
  return query
}

// Filtered client-side: the server's latest_cycle filter disagrees with billing_cycle.
export function usePaymentIssueItems(enabled: boolean) {
  const query = useOrderItems(PAYMENT_ISSUE_PARAMS, enabled)
  const items = (query.data ?? []).filter(item => hasFailedRebill(item) && isLatestCycle(item))
  return { ...query, data: items }
}

// One card per item — no grouping by subscription_id.
export function useSubscriptions() {
  const query = useOrderItems(LATEST_CYCLE_PARAMS)
  const items = sortItems(query.data ?? [])
  const active = items.filter(isActiveSubscription)
  const inactive = items.filter(item => !isActiveSubscription(item))
  return { ...query, items, active, inactive }
}

export interface OrderGroup {
  order: any
  items: Subscription[]
}

// One card per order, each holding its own item rows. No billing_cycle_type filter on
// purpose — an order mixes one-time and recurring items and the card lists all of them.
export function useOrderGroups(subscriptionId?: number | string) {
  const query = useOrderItems(orderGroupParams(subscriptionId))
  const items = sortItems(query.data ?? [])
  const groups: OrderGroup[] = []
  const byOrderId = new Map<number | string, OrderGroup>()

  items.forEach(item => {
    // An item with no order stands alone rather than collapsing into a shared bucket.
    const orderId = item.order?.id ?? `item-${item.id}`
    const existing = byOrderId.get(orderId)
    if (existing) {
      existing.items.push(item)
      return
    }
    const group: OrderGroup = { order: item.order ?? {}, items: [item] }
    byOrderId.set(orderId, group)
    groups.push(group)
  })

  return { ...query, items, groups }
}

// A failed request (endpoint missing on an environment, source without offers) leaves `data`
// empty, which hides the section rather than showing an error.
export function useCrossSellProducts(sourceCampaignProductId: number | undefined) {
  return useQuery({
    ...crossSellQuery(sourceCampaignProductId),
    select: toCrossSellProducts,
    enabled: sourceCampaignProductId != null,
  })
}

export function usePortalItem(itemId: string | number | undefined) {
  const query = useQuery({ ...portalItemQuery(itemId!), enabled: !!itemId })

  const routed = query.data as Subscription | undefined
  const superseded =
    !!routed &&
    isSupersededCycle(routed) &&
    (isRecurring(routed as any) || Number(routed.billing_cycle_type) === BillingCycleType.Recurring)
  const latestQuery = useOrderItems(LATEST_CYCLE_PARAMS, superseded)
  const subscriptionId = (routed as any)?.subscription_id
  const latest =
    superseded && subscriptionId != null
      ? latestQuery.data?.find(item => (item as any).subscription_id === subscriptionId)
      : undefined

  const data = latest ?? routed
  usePreloadedThumbnails(data ? [data] : undefined)
  return { ...query, data, isLoading: query.isLoading || (superseded && latestQuery.isLoading) }
}

export function useOrderTransactions(itemId?: number | string) {
  return useQuery({
    queryKey: [...queryKeys.portal, 'transactions', itemId],
    queryFn: () => subscriptionService.getTransactions(itemId!),
    enabled: itemId != null,
    ...PORTAL_CACHE,
  })
}

export function useOrderItemsTransactions(itemIds: (number | string)[]) {
  const results = useQueries({
    queries: itemIds.map(id => ({
      queryKey: [...queryKeys.portal, 'transactions', id],
      queryFn: () => subscriptionService.getTransactions(id),
      enabled: id != null,
      ...PORTAL_CACHE,
    })),
  })
  return results.flatMap(r => (r.data as any[]) || [])
}
